/**
 * InquiryWarning Component
 * 
 * Warning modal shown when user attempts to dispute an inquiry
 * that appears linked to an open/current account.
 */

import React from 'react';
import {
  AlertTriangle,
  CreditCard,
  Building,
  Calendar,
  CheckCircle,
  Info
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';

/**
 * Single Inquiry Warning Modal
 */
export const InquiryWarningModal = ({ 
  open, 
  onClose, 
  onProceed,
  inquiry,
  linkedAccount
}) => {
  const creditorName = inquiry?.company_name || inquiry?.creditor || 'Unknown';
  const inquiryDate = inquiry?.inquiry_date || inquiry?.date || 'Unknown';
  const accountStatus = linkedAccount?.account_status || linkedAccount?.status || 'Open';
  const accountType = linkedAccount?.account_type || 'Unknown';
  
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-lg" data-testid="inquiry-warning-modal">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={24} className="text-amber-600" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-stone-900">
              This Inquiry May Be Tied to an Open Account
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 mt-4">
              {/* Warning Message */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  Disputing legitimate inquiries tied to active accounts, especially credit cards,
                  may be unnecessary and could create account relationship risk. Only dispute if
                  you believe this inquiry is truly unauthorized.
                </p>
              </div>
              
              {/* Matched Context */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">Matched Information:</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <Building size={12} />
                      <span>Creditor</span>
                    </div>
                    <p className="text-sm font-medium text-stone-800">{creditorName}</p>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <Calendar size={12} />
                      <span>Inquiry Date</span>
                    </div>
                    <p className="text-sm font-medium text-stone-800">{inquiryDate}</p>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <CheckCircle size={12} />
                      <span>Matched Account Status</span>
                    </div>
                    <p className="text-sm font-medium text-emerald-600">{accountStatus}</p>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <CreditCard size={12} />
                      <span>Account Type</span>
                    </div>
                    <p className="text-sm font-medium text-stone-800">{accountType}</p>
                  </div>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <AlertDialogFooter className="mt-6 flex-col sm:flex-row gap-2">
          <AlertDialogCancel asChild>
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full sm:w-auto"
              data-testid="inquiry-warning-cancel"
            >
              Cancel
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button 
              onClick={onProceed}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="inquiry-warning-proceed"
            >
              <AlertTriangle size={16} className="mr-2" />
              Proceed Anyway
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Bulk Inquiry Warning Modal
 * Shows when selected inquiries include some linked to open accounts
 */
export const BulkInquiryWarningModal = ({ 
  open, 
  onClose, 
  onDisputeSafe,
  onDisputeAll,
  safeInquiries = [],
  cautionInquiries = []
}) => {
  const totalSelected = safeInquiries.length + cautionInquiries.length;
  
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-xl" data-testid="bulk-inquiry-warning-modal">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={24} className="text-amber-600" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold text-stone-900">
              Some Inquiries May Be Tied to Open Accounts
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-600">Selected inquiries:</span>
                  <span className="font-semibold text-stone-800">{totalSelected}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-2xl font-bold text-emerald-700">{safeInquiries.length}</p>
                    <p className="text-xs text-emerald-600">Safe to dispute</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-2xl font-bold text-amber-700">{cautionInquiries.length}</p>
                    <p className="text-xs text-amber-600">Need review</p>
                  </div>
                </div>
              </div>
              
              {/* Caution List */}
              {cautionInquiries.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Inquiries linked to open accounts:
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {cautionInquiries.map((inquiry, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg text-sm">
                        <span className="font-medium text-stone-800">
                          {inquiry.company_name || inquiry.creditor}
                        </span>
                        <span className="text-xs text-amber-600 px-2 py-0.5 bg-amber-100 rounded-full">
                          {inquiry.analysis?.tag || 'Linked Account'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Warning */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info size={16} className="text-amber-600 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Disputing inquiries tied to active accounts may be unnecessary and could
                    affect your relationship with the creditor.
                  </p>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <AlertDialogFooter className="mt-6 flex-col gap-2">
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full sm:w-auto"
              data-testid="bulk-warning-cancel"
            >
              Cancel
            </Button>
            
            {safeInquiries.length > 0 && (
              <Button 
                onClick={onDisputeSafe}
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="bulk-dispute-safe"
              >
                <CheckCircle size={16} className="mr-2" />
                Dispute Safe Only ({safeInquiries.length})
              </Button>
            )}
            
            <Button 
              onClick={onDisputeAll}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-dispute-all"
            >
              <AlertTriangle size={16} className="mr-2" />
              Proceed with All ({totalSelected})
            </Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default InquiryWarningModal;
